<template>
    <v-dialog v-model="isOpen" scrollable>
        <template #default>
            <v-card class="mx-auto" width="60%">
                <v-card-title><b>Neuen Studiengang anlegen</b></v-card-title>
                <v-card-text>
                    <studiengang-form
                        v-model="editStudiengang"
                        :is-loading="isLoading"
                        :available-studienformen="availableStudienformen"
                        :availableAbschlussArten="availableAbschlussArten"
                        :available-standorte="availableStandorte"
                        :available-fachbereiche="availableFachbereiche"
                    />
                </v-card-text>
                <v-card-actions class="d-flex flex-row justify-space-between px-6">
                    <v-btn
                        class="mr-auto"
                        type="reset"
                        color="primary"
                        variant="outlined"
                        :disabled="isLoading"
                        @click="resetEditStudiengang"
                    >
                        Reset
                    </v-btn>
                    <v-btn
                        variant="tonal"
                        :loading="isLoading"
                        color="secondary"
                        @click="closeModal"
                    >
                        Abbrechen
                    </v-btn>
                    <v-btn
                        variant="tonal"
                        :loading="isLoading"
                        color="primary"
                        @click="handleSaveStudiengang"
                    >
                        Speichern
                    </v-btn>
                </v-card-actions>
            </v-card>
        </template>
    </v-dialog>
</template>

<script setup lang="ts">
    import { CreateStudiengangDto } from '@/js/types/CreateStudiengangDto'
    import { computed, ref } from 'vue'
    import { useAlertStore, useStudiengangStore, useFachbereichStore } from '@/js/stores'
    import StudiengangForm from '@/js/components/Studiengang/StudiengangForm.vue'
    import { getViolationErrorsObject } from '@/js/utils'

    const props = defineProps({
        modelValue: {
            type: Boolean,
            required: true
        }
    })

    const emit = defineEmits(['update:modelValue'])

    const alertStore = useAlertStore()
    const studiengangStore = useStudiengangStore()
    const fachbereichStore = useFachbereichStore()

    const isOpen = computed({
        get: () => props.modelValue,
        set: (value) => emit('update:modelValue', value)
    })

    const isLoading = computed(() => studiengangStore.isLoading)
    const availableStudienformen = computed(() => studiengangStore.availableStudienformen)
    const availableAbschlussArten = computed(() => studiengangStore.availableAbschlussArten)
    const availableFachbereiche = computed(() => fachbereichStore.availableFachbereiche)

    const availableStandorte = ['Köthen', 'Bernburg', 'Dessau', 'Online']

    const createEmptyEditStudiengang = () => ({
        name: '',
        kurzName: '',
        standort: '',
        fachbereich: '',
        abschlussArt: {
            abschluss: '',
            abschlussLang: ''
        },
        studienformen: {
            studienformen: []
        }
    })

    const editStudiengang = ref(createEmptyEditStudiengang())

    const resetEditStudiengang = () => {
        editStudiengang.value = createEmptyEditStudiengang()
    }

    const createError = computed(() => studiengangStore.getError('create-studiengang'))
    const validationErrors = computed(() => getViolationErrorsObject(createError.value?.errorData))

    const closeModal = () => {
        isOpen.value = false
    }

    const handleSaveStudiengang = async () => {
        try {
            const payload: CreateStudiengangDto = {
                fachbereich: editStudiengang.value.fachbereich,
                name: editStudiengang.value.name,
                kurzName: editStudiengang.value.kurzName,
                standort: editStudiengang.value.standort,
                abschlussArt: editStudiengang.value.abschlussArt?.abschluss ?? '',
                studienformen: editStudiengang.value.studienformen?.studienformen ?? []
            }
            await studiengangStore.createStudiengang(payload)
            alertStore.success('Studiengang erfolgreich angelegt.', 'Info')
            isOpen.value = false
            resetEditStudiengang()
        } catch {
            const error = studiengangStore.getError('create-studiengang')
            alertStore.error(error?.message ?? 'Fehler beim Anlegen des Studiengangs.', 'Fehler!')
        }
    }
</script>
